import { Component } from "react"
import { v4 as uuid } from "uuid"
import PropTypes from "prop-types"
import classNames from "classnames"
import { connect } from "react-redux"
import { get, chain, isNil, map } from "lodash"
import moment from "moment"
import resizable from "hocs/resizable"
import tooltipContainer from "hocs/tooltip-container"
import Tooltip from "components/charts/tooltip"
import { DATE } from "helpers/shapes"
import { LINE, HIGHLIGHTER } from "../shapes"
import LegendItem from "./legend-item"
import ChartDefs from "./components/chart-defs"
import Axis from "./axis"
import Dots from "./dots"
import { getData, getLines, getXAxis, getYAxis, getScaleX, getScaleY, getFindByOffset, getHighlighter, getOnlyLegendText, getThresholds, CHART_LEFT_PADDING } from "./helper"
import { findClosestDatum } from "../helpers/find-closest-datum"
import "./style.scss"
import { THRESHOLD_PARAMS } from "helpers/threshold"

const AXIS = PropTypes.shape({
  fn: PropTypes.func.isRequired,
  transform: PropTypes.string.isRequired,
  baseline: PropTypes.shape({
    x1: PropTypes.number.isRequired,
    x2: PropTypes.number.isRequired,
    y1: PropTypes.number.isRequired,
    y2: PropTypes.number.isRequired,
  }).isRequired,
})

const THRESHOLD = PropTypes.shape({
  x: PropTypes.number.isRequired,
  width: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  className: PropTypes.string.isRequired,
})

export class MultiLineChart extends Component {
  static propTypes = {
    lines: PropTypes.arrayOf(LINE).isRequired,
    data: PropTypes.array.isRequired,
    highlighter: HIGHLIGHTER,
    startDate: DATE,
    endDate: DATE,
    isPeriodIntraday: PropTypes.bool,
    height: PropTypes.number.isRequired,
    width: PropTypes.number,
    resizableRef: PropTypes.func.isRequired,
    xAxis: AXIS.isRequired,
    yAxis: AXIS.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
    })).isRequired,
    tooltip: PropTypes.element.isRequired,
    scaleX: PropTypes.func.isRequired,
    scaleY: PropTypes.func.isRequired,
    findByOffset: PropTypes.func.isRequired,
    toggle: PropTypes.func.isRequired,
    subscribe: PropTypes.func.isRequired,
    unsubscribe: PropTypes.func.isRequired,
    setDate: PropTypes.func.isRequired,
    focusChartLine: PropTypes.func.isRequired,
    toggleOnly: PropTypes.func.isRequired,
    blurChartLine: PropTypes.func.isRequired,
    focusedChartLine: PropTypes.string,
    onlyLegendText: PropTypes.string.isRequired,
    thresholdParams: THRESHOLD_PARAMS,
    thresholds: PropTypes.arrayOf(THRESHOLD).isRequired,
    rightLegendItems: PropTypes.array,
    onDotClick: PropTypes.func,
    // Single-day intraday timelapse: the chart is a static point-in-time view, so suppress the
    // graph-click (set-date) and the pointer cursor that would imply it's interactive.
    isOneDayMode: PropTypes.bool,
  }

  legendRef = null

  constructor(props) {
    super(props)

    const chartId = uuid()
    this.pathGradientId = `path-gradient-${chartId}`
    this.axisGradientId = `axis-gradient-${chartId}`
  }

  findByOffset = offset => {
    const { data, scaleX } = this.props
    // Offsets must arrive container-relative; subtract CHART_LEFT_PADDING to land in SVG plot coordinates.
    const lookup = scaleX.invert(offset - CHART_LEFT_PADDING)
    return findClosestDatum(data, lookup)
  }

  findDots = offset => {
    const { scaleX, scaleY, items, findByOffset } = this.props

    const datum = findByOffset(offset)

    if (datum) {
      return {
        x: scaleX(datum.date),
        dots: items
          .filter(item => !item.disabled && !isNil(datum[item.id]))
          .map(item => ({ id: item.id, color: item.color, y: scaleY(get(datum, [item.id], 0)) })),
      }
    }
  }

  setDate = offset => {
    const { setDate } = this.props
    const { date } = this.findByOffset(offset)
    // moment(date) — local mode is correct: transformDataPoint builds datum.date as a
    // local-midnight moment from a YYYY-MM-DD batch string, so local format gives the right date
    // in any timezone. Both whole-plot clicks and dot clicks use the same convention.
    setDate(moment(date).format("YYYY-MM-DD"))
  }

  handleGraphClick = ev => {
    const box = ev.currentTarget.getBoundingClientRect()
    // Deliberately 16, not CHART_LEFT_PADDING (18): the whole-plot date pick is empirically
    // calibrated to resolve 2px left of the cursor. Using the full gap shifts the selected date
    // a day later near the plot edge (regression app-3165).
    const offsetX = ev.clientX - box.left + 16
    this.setDate(offsetX)
  }

  handleDotClick = (event, lineId) => {
    event.stopPropagation()
    const box = event.currentTarget.closest("svg").getBoundingClientRect()
    // clientX (not pageX): box is viewport-relative, so pageX over-shoots by the horizontal scroll.
    // +CHART_LEFT_PADDING converts this SVG-relative coordinate into the container-relative basis
    // findByOffset expects, matching the hover offset coordinate system.
    const offsetX = event.clientX - box.left + CHART_LEFT_PADDING
    const datum = this.findByOffset(offsetX)
    // datum.date is a local-midnight moment built from a YYYY-MM-DD batch string (transformDataPoint).
    // Local-mode format gives the correct batch date in any timezone.
    const date = datum ? moment(datum.date).format("YYYY-MM-DD") : undefined
    // batchId survives the day-level date format above, so batch-trend points stay disambiguable.
    this.props.onDotClick?.({ clientX: event.clientX, clientY: event.clientY, date, batchId: datum?.batchId }, lineId)
  }

  computeAxisStyle = () => {
    const { highlighter, width } = this.props
    const axisStyle = {}

    if (highlighter && width) {
      axisStyle.stroke = `url(#${this.axisGradientId})`
    }

    return axisStyle
  }

  computePathStyle = (id, initialStyle) => {
    const { highlighter, width } = this.props
    const pathStyle = {}

    if (highlighter && width) {
      pathStyle.stroke = `url(#${id})`
    } else {
      pathStyle.stroke = initialStyle
    }

    return pathStyle
  }

  registerLegendRef = element => { this.legendRef = element }

  render() {
    const {
      data,
      resizableRef,
      lines,
      xAxis,
      yAxis,
      items,
      toggle,
      focusedChartLine,
      focusChartLine,
      blurChartLine,
      subscribe,
      unsubscribe,
      highlighter,
      tooltip,
      width,
      toggleOnly,
      onlyLegendText,
      thresholdParams,
      rightLegendItems,
      thresholds,
      onDotClick,
      isOneDayMode,
    } = this.props

    if (!data.length) {
      return <div ref={resizableRef} className="components_charts_multi-line no-data">No significant data was found. Adjust the date range to a larger time frame to view graph.</div>
    } else {
      return <div className="components_charts_multi-line">
        <div className="graph-top">
          <div className="legend" ref={this.registerLegendRef}>
            {items.map(item =>
              <LegendItem
                onlyLegendText={onlyLegendText}
                key={item.id}
                {...item}
                onClick={toggle}
                onOnlyToggle={toggleOnly}
                focus={focusChartLine}
                blur={blurChartLine}
                focused={focusedChartLine}
              />,
            )}
          </div>
          <div className="right-legend">
            {thresholdParams
              && <div className="right-legend-item">
                <span className="title">THRESHOLDS</span>
                <span className="contents">{`${thresholdParams.value}${thresholdParams.isPercent ? "%" : ""}`}</span>
              </div>}
            {map(rightLegendItems, item =>
              <div className="right-legend-item">
                {item}
              </div>)}
          </div>
        </div>
        <svg
          ref={resizableRef}
          onClick={isOneDayMode ? undefined : this.handleGraphClick}
          className={classNames("multi-line-chart", isOneDayMode && "static")}
        >
          {thresholds.map((threshold, index) => <rect key={`threshold-${index}`} {...threshold} />)}
          <ChartDefs lines={lines} axisId={this.axisGradientId} pathsId={this.pathGradientId} offset={highlighter && highlighter.x1} chartWidth={width} />
          <Axis {...xAxis} />
          <Axis {...yAxis} />
          <line {...xAxis.baseline} className="baseline" style={this.computeAxisStyle()} />
          <line {...yAxis.baseline} className="baseline" />
          {/* Display hidden lines first so that the actual lines are over them */}
          {chain(lines).filter(l => l.missing).map((line) =>
            <path
              key={`${line.id}-missing`}
              className={classNames(
                "path missing",
                focusedChartLine && focusedChartLine !== line.id && "hidden",
              )}
              d={line.missing}
            />,
          ).value()}

          {lines.map((line, index) =>
            <g key={line.id}>
              <path
                className={classNames(
                  "path focus-enabled",
                  focusedChartLine && focusedChartLine === line.id && "visible",
                  focusedChartLine && focusedChartLine !== line.id && "hidden",
                  data.length === 1 && "single-dot",
                )}
                {...line}
                style={{ ...this.computePathStyle(`${this.pathGradientId}-${index}`, line.style.stroke) }}
              />
              {/* Intraday: a wide transparent hit band along each line, so the drilldown can be opened
                  by clicking the line anywhere — capturing that exact line's id (and thus its sort) —
                  rather than landing on a tiny, densely-packed dot. */}
              {isOneDayMode && onDotClick && (
                <path
                  d={line.d}
                  className="line-hit-area"
                  onMouseEnter={() => focusChartLine(line.id)}
                  onMouseLeave={blurChartLine}
                  onClick={(event) => this.handleDotClick(event, line.id)}
                />
              )}
            </g>,
          )}
          {highlighter && <line className="highlighter" {...highlighter} />}
          <Dots
            {...{
              subscribe,
              unsubscribe,
              findDots: this.findDots,
              focus: focusChartLine,
              blur: blurChartLine,
              focused: focusedChartLine,
              onDotClick: onDotClick ? this.handleDotClick : undefined,
            }}
          />
        </svg>
        <Tooltip {...{ legendRef: this.legendRef, subscribe, unsubscribe, findByOffset: this.findByOffset, position: "right" }}>
          {tooltip}
        </Tooltip>
      </div>
    }
  }
}

const mapStateToProps = (state, props) => ({
  data: getData(props),
  lines: getLines(props),
  xAxis: getXAxis(props),
  yAxis: getYAxis(props),
  scaleX: getScaleX(props),
  scaleY: getScaleY(props),
  findByOffset: getFindByOffset(props),
  highlighter: getHighlighter(props),
  onlyLegendText: getOnlyLegendText(props),
  thresholds: getThresholds(props),
})

export default tooltipContainer(resizable(connect(mapStateToProps)(MultiLineChart)))
